/* eslint-disable no-console */
import { CHARTS_API } from 'constants/api';
import { constructQueryString, noop } from 'helpers/utils';

import {
  calculateFromTime,
  calculateToTime,
  getChartCacheTTL,
  transformChartBars,
} from './helper';

const requester = async (url: string) => {
  try {
    const response = await fetch(url);
    const responseTest = await response.text();
    return JSON.parse(responseTest).result;
  } catch (error) {
    console.error('[requester]: Method call error', error);
    return null;
  }
};
/**
 * ref: https://www.tradingview.com/charting-library-docs/latest/connecting_data/Datafeed-API
 */
export type PeriodParamsWithOptionalCountback = Omit<any, 'countBack'> & {
  countBack?: number;
};

const getSupportedResolution = data => {
  if (data.supported_resolutions) {
    return data.supported_resolutions;
  }
  return [
    '1',
    '10',
    '120',
    '15',
    '180',
    '1W',
    '240',
    '2W',
    '3',
    '30',
    '30D',
    '360',
    '45',
    '5',
    '60',
    '720',
    '7D',
    '90',
    'D',
    'W',
  ];
};

const fundingTVDatafeed = () => {
  return {
    onReady: async (callback: any) => {
      console.log('DEBUG [onReady]: Method call');
      const configurationData = await requester(CHARTS_API.CONFIG);
      setTimeout(() => callback(configurationData), 0);
    },
    searchSymbols: async (
      userInput: string,
      exchange: string,
      symbolType: string,
      onResultReadyCallback: any
    ) => {
      const queryString = {
        limit: 30,
        query: userInput,
        exchange,
        symbolType,
      };
      const api = constructQueryString(CHARTS_API.COMPARE_SYMBOL, queryString);
      const data = await requester(api);
      console.log('DEBUG [searchSymbols]: Method call', {
        userInput,
        exchange,
        symbolType,
        data,
      });
      setTimeout(() => {
        onResultReadyCallback(data);
      }, 0);
    },
    resolveSymbol: async (
      symbolName: string,
      onSymbolResolvedCallback: any,
      onResolveErrorCallback: any
    ) => {
      try {
        const symbol = symbolName.includes('Delta:')
          ? symbolName.split(':')[1]
          : symbolName;
        console.log('DEBUG [resolveSymbol]: Method call', symbolName);
        const data = await requester(`${CHARTS_API.SEARCH_SYMBOL}?symbol=${symbol}`);
        if (data) {
          const symbolInfo: any = {
            ...data,
            base_name: `Delta:${data.name}`,
            full_name: data.name,
            listed_exchange: 'Delta',
            exchange: 'Delta',
            currency_code: data.currency_code ?? data['currency-code'],
            original_currency_code: data.currency_code ?? data['currency-code'],
            supported_resolutions: getSupportedResolution(data),
            format: data.format ?? 'price',
          };
          setTimeout(() => {
            onSymbolResolvedCallback(symbolInfo);
          }, 0);
        } else {
          setTimeout(() => {
            onResolveErrorCallback('Not found');
          }, 0);
        }
      } catch (error) {
        console.error('[resolveSymbol]: Method call error', error);
        onResolveErrorCallback('Not found');
      }
    },
    getBars: async (
      symbolInfo: any,
      resolution: string,
      periodParams: PeriodParamsWithOptionalCountback,
      onHistoryCallback: any,
      onErrorCallback: any
    ) => {
      try {
        const apiResolution = resolution === '1D' ? 'D' : resolution;
        const chartCacheTTL = getChartCacheTTL(apiResolution);
        const { to, countBack } = periodParams;
        const toTime = calculateToTime({
          to,
          resolution: apiResolution,
        });
        const fromTime = calculateFromTime({
          countBack,
          to,
          resolution: apiResolution,
        });

        const queryParams = {
          symbol: symbolInfo.name,
          resolution: apiResolution,
          from: fromTime,
          to: toTime,
          cache_ttl: chartCacheTTL,
        };
        const getBarsUrl = constructQueryString(CHARTS_API.HISTORY_SYMBOL, queryParams);
        const data = await requester(getBarsUrl);

        if (data && data.t.length > 0) {
          let bars: any[] = transformChartBars(data).filter(
            bar => bar.time >= fromTime * 1000 && bar.time < toTime * 1000
          );

          if (countBack && bars.length < countBack) {
            const additionalBarsNeeded = countBack - bars.length;
            const earliestBarTime = bars[0].time / 1000; // Convert back to seconds
            const additionalBarsUrl = constructQueryString(CHARTS_API.HISTORY_SYMBOL, {
              symbol: symbolInfo.name,
              resolution: apiResolution,
              from: earliestBarTime - additionalBarsNeeded * 60 * 60 * 24,
              to: earliestBarTime,
            });
            const additionalBars = await requester(additionalBarsUrl);
            if (additionalBars && additionalBars.t.length > 0) {
              const additionalBarsArray = transformChartBars(additionalBars);
              bars = [...additionalBarsArray, ...bars];
            }
          }

          const noData = bars.length === 0;
          setTimeout(() => {
            onHistoryCallback(noData ? [] : bars, { noData });
          }, 0);
        } else {
          onHistoryCallback([], { noData: true });
        }
      } catch (error) {
        console.error('[getBars]: Method call error', error);
        onErrorCallback(error);
      }
    },
    subscribeBars: () => {
      // ? added to match type, method not being used
      noop();
    },
    unsubscribeBars: () => {
      // ? added to match type, method not being used
      noop();
    },
  };
};

export default fundingTVDatafeed;
